import React, { useState } from 'react';
import { Package, MapPin, Mail, Phone, User, Calendar, DollarSign } from 'lucide-react';
import './OrderManagement.css';

function OrderManagement({ orders, onUpdateStatus }) {
  const [selectedOrder, setSelectedOrder] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      shipped: '#8b5cf6',
      delivered: '#10b981',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (orders.length === 0) {
    return (
      <div className="empty-state">
        <Package size={64} color="#d1d5db" />
        <h2>No orders yet</h2>
        <p>Orders will appear here when customers place them.</p>
      </div>
    );
  }

  return (
    <div className="order-management">
      <div className="orders-list">
        {orders.map(order => (
          <div
            key={order.id}
            className={`order-card ${selectedOrder?.id === order.id ? 'selected' : ''}`}
            onClick={() => setSelectedOrder(order)}
          >
            <div className="order-header">
              <div className="order-id">Order #{order.id.slice(-6).toUpperCase()}</div>
              <div
                className="order-status-badge"
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {order.status}
              </div>
            </div>

            <div className="order-info">
              <div className="info-item">
                <User size={16} />
                <span>{order.customer?.name}</span>
              </div>
              <div className="info-item">
                <Calendar size={16} />
                <span>{formatDate(order.createdAt)}</span>
              </div>
              <div className="info-item">
                <DollarSign size={16} />
                <span className="order-total">${order.total?.toFixed(2)}</span>
              </div>
            </div>

            <div className="order-items-preview">
              {order.items?.slice(0, 2).map((item, idx) => (
                <div key={idx} className="item-preview">
                  {item.name} x {item.quantity}
                </div>
              ))}
              {order.items?.length > 2 && (
                <div className="more-items">+{order.items.length - 2} more</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedOrder && (
        <div className="order-details">
          <div className="details-header">
            <h2>Order Details</h2>
            <div
              className="status-badge-large"
              style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
            >
              {selectedOrder.status}
            </div>
          </div>

          <div className="details-section">
            <h3><User size={20} /> Customer Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Name:</label>
                <span>{selectedOrder.customer?.name}</span>
              </div>
              <div className="detail-item">
                <label><Mail size={16} /> Email:</label>
                <span>{selectedOrder.customer?.email}</span>
              </div>
              <div className="detail-item">
                <label><Phone size={16} /> Phone:</label>
                <span>{selectedOrder.customer?.phone}</span>
              </div>
            </div>
          </div>

          <div className="details-section">
            <h3><MapPin size={20} /> Delivery Address</h3>
            <div className="address-box">
              <p>{selectedOrder.customer?.address}</p>
              <p>{selectedOrder.customer?.city}</p>
              {selectedOrder.customer?.notes && (
                <p className="notes">
                  <strong>Notes:</strong> {selectedOrder.customer.notes}
                </p>
              )}
            </div>
          </div>

          <div className="details-section">
            <h3><Package size={20} /> Order Items</h3>
            <div className="items-list">
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} className="item-row">
                  <div className="item-info">
                    <div className="item-name">{item.name}</div>
                    <div className="item-category">{item.category}</div>
                  </div>
                  <div className="item-quantity">x{item.quantity}</div>
                  <div className="item-price">${(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="order-total-row">
              <span>Total:</span>
              <span className="total-amount">${selectedOrder.total?.toFixed(2)}</span>
            </div>
          </div>

          <div className="details-section">
            <h3>Update Order Status</h3>
            <div className="status-buttons">
              {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
                <button
                  key={status}
                  className={`status-btn ${selectedOrder.status === status ? 'active' : ''}`}
                  style={{
                    borderColor: getStatusColor(status),
                    color: selectedOrder.status === status ? 'white' : getStatusColor(status),
                    backgroundColor: selectedOrder.status === status ? getStatusColor(status) : 'transparent'
                  }}
                  onClick={() => onUpdateStatus(selectedOrder.id, status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          <div className="order-meta">
            <div className="meta-item">
              <label>Order ID:</label>
              <span>{selectedOrder.id}</span>
            </div>
            <div className="meta-item">
              <label>Created:</label>
              <span>{formatDate(selectedOrder.createdAt)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderManagement;
